import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  CircularProgress,
  IconButton,
  Dialog,
  DialogContent,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  YouTube as YouTubeIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const VideosPage = ({playlist, title}) => {
  const API_KEY = "AIzaSyAeIapHDru049HtEp5S4sKklzGbq_WNPzM";
  const PLAYLIST_ID = playlist;
  const RESULTS_PER_PAGE = 12; // Reducido para mejorar el rendimiento y UX

  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [nextPageToken, setNextPageToken] = useState('');
  const [hasMoreVideos, setHasMoreVideos] = useState(true);
  
  // Referencia al observador de intersección para el scroll infinito
  const observer = useRef();
  // Referencia al último elemento de video en la lista
  const lastVideoElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMoreVideos && !searchTerm) {
        fetchMoreVideos();
      }
    }, { threshold: 0.5 });
    
    if (node) observer.current.observe(node);
  }, [loading, hasMoreVideos, searchTerm]);
  
  useEffect(() => {
    // Reiniciar estados al cambiar de playlist
    setVideos([]);
    setFilteredVideos([]);
    setNextPageToken('');
    setHasMoreVideos(true);
    setInitialLoading(true);
    
    // Cargar videos iniciales
    fetchPlaylistVideos();
    
    // Cleanup
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [playlist]);

  useEffect(() => {
    if (videos.length > 0) {
      if (searchTerm) {
        const filtered = videos.filter(video => 
          video.snippet.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          video.snippet.description.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredVideos(filtered);
      } else {
        setFilteredVideos(videos);
      }
    }
  }, [searchTerm, videos]);

  const fetchPlaylistVideos = async () => {
    try {
      setLoading(true);
      const url = `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&maxResults=${RESULTS_PER_PAGE}&playlistId=${PLAYLIST_ID}&key=${API_KEY}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Error al cargar los videos de la playlist');
      }
      
      const data = await response.json();
      
      setVideos(data.items || []);
      setFilteredVideos(data.items || []);
      setNextPageToken(data.nextPageToken || '');
      setHasMoreVideos(!!data.nextPageToken);
      setLoading(false);
      setInitialLoading(false);
    } catch (error) {
      console.error('Error fetching YouTube playlist:', error);
      setError(error.message);
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const fetchMoreVideos = async () => {
    if (!nextPageToken || loading || !hasMoreVideos) return;
    
    try {
      setLoading(true);
      const url = `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&maxResults=${RESULTS_PER_PAGE}&playlistId=${PLAYLIST_ID}&key=${API_KEY}&pageToken=${nextPageToken}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Error al cargar más videos');
      }
      
      const data = await response.json();
      
      setVideos(prevVideos => [...prevVideos, ...(data.items || [])]);
      
      if (!searchTerm) {
        setFilteredVideos(prevFiltered => [...prevFiltered, ...(data.items || [])]);
      }
      
      setNextPageToken(data.nextPageToken || '');
      setHasMoreVideos(!!data.nextPageToken);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching more videos:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  return (
    <Container component="main" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center" noWrap
        sx={{ 
          fontSize: 'calc(16px + 2vmin)', // Tamaño base + ajuste responsivo
          textAlign: 'center',
          width: '100%',
          '@media (max-width: 600px)': {
            fontSize: 'calc(14px + 2vmin)',
          }
        }}
      >
        {title}
      </Typography>
      
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Buscar..."
        value={searchTerm}
        onChange={handleSearchChange}
        sx={{
          mb: 4,
          "& .MuiInputBase-input": {
            color: "#fff"
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "#fff"
            },
            "&:hover fieldset": {
              borderColor: "#fff"
            },
            "&.Mui-focused fieldset": {
              borderColor: "#fff"
            }
          },
          "& .MuiInputBase-input::placeholder": {
            color: "#fff",
            opacity: 0.7
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#fff' }} />
            </InputAdornment>
          ),
        }}
      />
      
      {initialLoading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress color="secondary" />
        </Box>
      ) : error ? (
        <Typography color="error" align="center">
          {error}
        </Typography>
      ) : (
        <>
          <Grid container spacing={3}>
            {filteredVideos.map((video, index) => {
              // Si es el último video, añadir ref para el observer
              const isLastElement = index === filteredVideos.length - 1;
              
              return (
                <Grid 
                  item 
                  xs={12} sm={6} md={4} 
                  key={`${video.id}-${index}`}
                  ref={isLastElement && !searchTerm ? lastVideoElementRef : null}
                >
                  <Card 
                    elevation={2}
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.02)',
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={video.snippet.thumbnails.high?.url || video.snippet.thumbnails.default?.url}
                      alt={video.snippet.title}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleVideoClick(video)}
                    />
                    <CardActions 
                      sx={{ 
                        backgroundColor: '#fff',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 2,
                        py: 1
                      }}
                    >
                      <Button 
                        size="small" 
                        color="secondary"
                        onClick={() => handleVideoClick(video)}
                        sx={{ 
                          color: '#000',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        Ver Video
                      </Button>
                      <Button 
                        size="small"
                        href={`https://www.youtube.com/watch?v=${video.snippet.resourceId.videoId}&list=${PLAYLIST_ID}`}
                        target="_blank"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          textAlign: 'center',
                          gap: '4px'
                        }}
                      >
                        Ver en YouTube <YouTubeIcon fontSize="small" />
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          
          {filteredVideos.length === 0 && !initialLoading && (
            <Box width="100%" textAlign="center" my={4}>
              <Typography variant="h6">
                No se encontraron videos que coincidan con tu búsqueda.
              </Typography>
            </Box>
          )}
          
          {loading && !initialLoading && (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress color="secondary" size={30} />
            </Box>
          )}
          
          {!hasMoreVideos && filteredVideos.length > 0 && !searchTerm && (
            <Box width="100%" textAlign="center" my={4}>
              <Typography variant="body2" color="textSecondary">
                Has llegado al final de la lista de videos.
              </Typography>
            </Box>
          )}
        </>
      )}
      
      {/* Video Dialog */}
      <Dialog
        fullWidth
        maxWidth="md"
        open={dialogOpen}
        onClose={handleDialogClose}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            aria-label="close"
            onClick={handleDialogClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)',
              },
              zIndex: 1
            }}
          >
            <CloseIcon />
          </IconButton>
          
          {selectedVideo && (
            <Box sx={{ position: 'relative', paddingTop: '56.25%', width: '100%' }}>
              <iframe
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 0
                }}
                src={`https://www.youtube.com/embed/${selectedVideo.snippet.resourceId.videoId}`}
                title={selectedVideo.snippet.title}
                allowFullScreen
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default VideosPage;